import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { authFetch } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const STATUS_COLORS = { initiated: '#C49A23', delivery_confirmed: '#3B82F6', funds_released: '#2D7A3A', disputed: '#C43A23', resolved: '#2D7A3A', cancelled: '#AAAAAA' };
const STATUS_LABELS = { initiated: 'Initiated', delivery_confirmed: 'Confirmed', funds_released: 'Released', disputed: 'Disputed', resolved: 'Resolved', cancelled: 'Cancelled' };

export default function AnalyticsCharts() {
  const [statusData, setStatusData] = useState([]);
  const [volumeData, setVolumeData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const f = async () => {
      try {
        const [sR, vR] = await Promise.all([authFetch(`${API}/api/analytics/status-breakdown`), authFetch(`${API}/api/analytics/volume-trend`)]);
        if (sR.ok) { const d = await sR.json(); setStatusData(d.breakdown.map(b => ({ name: STATUS_LABELS[b.status] || b.status, value: b.count, color: STATUS_COLORS[b.status] || '#999' }))); }
        if (vR.ok) { const d = await vR.json(); setVolumeData(d.trend.map(t => ({ date: t.date.slice(5), count: t.count, volume: Math.round(t.volume) }))); }
      } catch (e) {} finally { setLoading(false); }
    };
    f();
  }, []);

  const hasData = statusData.length > 0 || volumeData.length > 0;

  if (loading) return <div className="bg-tf-surface border border-tf-primary p-4 sm:p-6" data-testid="analytics-charts"><p className="font-mono text-[10px] text-tf-text-2 text-center tracking-[0.2em] uppercase">Loading...</p></div>;
  if (!hasData) return (
    <div className="bg-tf-surface border border-tf-primary" data-testid="analytics-charts">
      <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-tf-primary"><h2 className="font-heading font-bold text-xs sm:text-sm tracking-[0.15em] sm:tracking-[0.2em] uppercase text-tf-text">Analytics</h2></div>
      <div className="px-4 py-6 sm:py-8 text-center"><p className="font-body text-sm text-tf-text-2">No data yet.</p></div>
    </div>
  );

  return (
    <div className="bg-tf-surface border border-tf-primary" data-testid="analytics-charts">
      <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-tf-primary"><h2 className="font-heading font-bold text-xs sm:text-sm tracking-[0.15em] sm:tracking-[0.2em] uppercase text-tf-text">Analytics</h2></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-0">
        {statusData.length > 0 && (
          <div className="p-4 sm:p-5 sm:border-r border-b sm:border-b-0 border-tf-border-muted" data-testid="status-chart">
            <h3 className="font-mono text-[8px] sm:text-[9px] tracking-[0.2em] uppercase text-tf-text-2 mb-3 sm:mb-4">Status Breakdown</h3>
            <div className="flex items-center gap-3 sm:gap-4">
              <ResponsiveContainer width={90} height={90}>
                <PieChart><Pie data={statusData} cx="50%" cy="50%" innerRadius={22} outerRadius={42} dataKey="value" stroke="none">
                  {statusData.map((e, i) => <Cell key={i} fill={e.color} />)}</Pie></PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-1">
                {statusData.map((e, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: e.color }} />
                    <span className="font-mono text-[9px] sm:text-[10px] text-tf-text">{e.name}</span>
                    <span className="font-heading font-bold text-[9px] sm:text-[10px] text-tf-text ml-auto">{e.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        {volumeData.length > 0 && (
          <div className="p-4 sm:p-5" data-testid="volume-chart">
            <h3 className="font-mono text-[8px] sm:text-[9px] tracking-[0.2em] uppercase text-tf-text-2 mb-3 sm:mb-4">Volume Trend</h3>
            <ResponsiveContainer width="100%" height={90}>
              <BarChart data={volumeData}>
                <XAxis dataKey="date" tick={{ fontSize: 8, fill: '#888', fontFamily: 'Space Mono' }} axisLine={{ stroke: '#D4D4D4' }} tickLine={false} />
                <YAxis hide />
                <Tooltip contentStyle={{ background: '#1A1A1A', border: 'none', borderRadius: 0, fontSize: 10, color: '#fff', fontFamily: 'Space Mono' }} />
                <Bar dataKey="count" fill="#002FA7" radius={0} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}
